import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { getSecureItem } from '@/utils/localStorage';
import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Plus, Upload, X, User, Search, Check, Trash2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from '@/components/ui/use-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { createOnboarding } from '@/services/onboarding.service';
import { candidateService } from '@/services/candidate.service';
import { getStatusesByCategory } from '@/services/status.service';


// Define the onboarding schema using Zod
const onboardingSchema = z.object({
  candidate: z.object({
    _id: z.string().min(1, 'Candidate is required'),
    name: z.string().optional(),
  }).refine((data) => {
    // Additional validation to ensure candidate has a valid ID
    return data._id && data._id.trim().length > 0;
  }, {
    message: 'Candidate is required',
    path: ['_id']
  }),
  companyEmail: z.string().email('Invalid email address'),
  previousEmployerDocuments: z.object({
    expLetterUrl: z.string().optional(),
    expLetterExpanded: z.boolean().optional(),
    relievingLetterUrl: z.string().optional(),
    relievingLetterExpanded: z.boolean().optional(),
    resignationAcceptanceUrl: z.string().optional(),
    resignationAcceptanceExpanded: z.boolean().optional(),
    firstMonthSalarySlipUrl: z.string().optional(),
    firstMonthSalarySlipExpanded: z.boolean().optional(),
    secondMonthSalarySlipUrl: z.string().optional(),
    secondMonthSalarySlipExpanded: z.boolean().optional(),
    thirdMonthSalarySlipUrl: z.string().optional(),
    thirdMonthSalarySlipExpanded: z.boolean().optional(),
  }).optional(),
  identityProofs: z.object({
    aadhaarNumber: z.string().min(1, 'Aadhaar number is required').regex(/^\d{4}\s?\d{4}\s?\d{4}$/, 'Please enter a valid 12-digit Aadhaar number'),
    aadhaarDocUrl: z.string().optional(),
    panNumber: z.string().min(1, 'PAN number is required').regex(/^[A-Z]{5}[0-9]{4}[A-Z]{1}$/, 'Please enter a valid PAN number (e.g., ABCDE1234F)'),
    panDocUrl: z.string().optional(),
  }),
  educationalCertificates: z.array(z.object({
    degree: z.string().min(1, 'Degree is required'),
    institution: z.string().min(1, 'Institution is required'),
    docUrl: z.string().optional(),
  })).optional(),
  tasks: z.array(z.string()).optional(),
  notes: z.string().optional(),
});

// Infer the type for form values
export type OnboardingFormValues = z.infer<typeof onboardingSchema>;

// Define props for the OnboardingForm component
interface OnboardingFormProps {
  initialData?: Partial<OnboardingFormValues>;
  onSubmit: (data: OnboardingFormValues) => Promise<void>;
  onCancel: () => void;
  isEdit?: boolean;
  candidates?: Array<{ _id: string; name: string }>;
  fileUrls: { [key: string]: File | string | null };
  setFileUrls: React.Dispatch<React.SetStateAction<{ [key: string]: File | string | null }>>;
  formInitialized: boolean;
  setFormInitialized: React.Dispatch<React.SetStateAction<boolean>>;
  onboardingId?: string; // Add onboarding ID prop
}

// Helper function to normalize onboarding data
const normalizeOnboardingData = (data: any): Partial<OnboardingFormValues> => {
  if (!data) return {};

  const normalized = {
    candidate: {
      _id: data.candidate?._id || data.candidate?.id || '',
      name: data.candidate?.name || data.candidate?.firstName + ' ' + data.candidate?.lastName || '',
    },
    companyEmail: data.companyEmail || '',
    previousEmployerDocuments: {
      expLetterUrl: data.previousEmployerDocuments?.expLetterUrl || '',
      expLetterExpanded: Boolean(data.previousEmployerDocuments?.expLetterExpanded) || false,
      relievingLetterUrl: data.previousEmployerDocuments?.relievingLetterUrl || '',
      relievingLetterExpanded: Boolean(data.previousEmployerDocuments?.relievingLetterExpanded) || false,
      resignationAcceptanceUrl: data.previousEmployerDocuments?.resignationAcceptanceUrl || '',
      resignationAcceptanceExpanded: Boolean(data.previousEmployerDocuments?.resignationAcceptanceExpanded) || false,
      firstMonthSalarySlipUrl: data.previousEmployerDocuments?.firstMonthSalarySlipUrl || '',
      firstMonthSalarySlipExpanded: Boolean(data.previousEmployerDocuments?.firstMonthSalarySlipExpanded) || false,
      secondMonthSalarySlipUrl: data.previousEmployerDocuments?.secondMonthSalarySlipUrl || '',
      secondMonthSalarySlipExpanded: Boolean(data.previousEmployerDocuments?.secondMonthSalarySlipExpanded) || false,
      thirdMonthSalarySlipUrl: data.previousEmployerDocuments?.thirdMonthSalarySlipUrl || '',
      thirdMonthSalarySlipExpanded: Boolean(data.previousEmployerDocuments?.thirdMonthSalarySlipExpanded) || false,
    },
    identityProofs: {
      aadhaarNumber: data.identityProofs?.aadhaarNumber || '',
      aadhaarDocUrl: data.identityProofs?.aadhaarDocUrl || '',
      panNumber: data.identityProofs?.panNumber || '',
      panDocUrl: data.identityProofs?.panDocUrl || '',
    },
    educationalCertificates: Array.isArray(data.educationalCertificates) ? data.educationalCertificates : [],
    tasks: Array.isArray(data.tasks) ? data.tasks.map((task: any) => {
      // If task is an object with _id, extract the _id
      if (typeof task === 'object' && task._id) {
        return task._id;
      }
      // If task is already a string, use it as is
      if (typeof task === 'string') {
        return task;
      }
      // If task is an object with id, extract the id
      if (typeof task === 'object' && task.id) {
        return task.id;
      }
      // Fallback: convert to string
      return String(task);
    }) : [],
    notes: data.notes || '',
  };

  return normalized;
};

// File upload component - moved outside to prevent recreation issues
const FileUploadComponent = ({
  fieldName,
  label,
  isRequired = false,
  fileUrls,
  isEdit,
  selectedAadhaarFileName,
  selectedPanFileName,
  certificateFileNames,
  previousEmployerFileNames,
  fileErrors,
  touchedFiles,
  uploadingFiles,
  handleDragOver,
  handleDragLeave,
  handleDrop,
  handleFileChange,
  removeFile }: {
    fieldName: string;
    label: string;
    placeholder: string;
    isRequired?: boolean;
    fileUrls: { [key: string]: File | string | null };
    setFileUrls: React.Dispatch<React.SetStateAction<{ [key: string]: File | string | null }>>;
    isEdit: boolean;
    selectedAadhaarFileName: string;
    selectedPanFileName: string;
    certificateFileNames: { [key: string]: string };
    previousEmployerFileNames: { [key: string]: string };
    fileErrors: { [key: string]: string };
    touchedFiles: { [key: string]: boolean };
    uploadingFiles: { [key: string]: boolean };
    handleDragOver: (e: React.DragEvent, fieldName: string) => void;
    handleDragLeave: (e: React.DragEvent, fieldName: string) => void;
    handleDrop: (e: React.DragEvent, fieldName: string) => void;
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => void;
    removeFile: (fieldName: string) => void;
    setValue: (name: any, value: any) => void;
  }) => {
  const file = fileUrls[fieldName];
  const fileName = fieldName === 'identityProofs.aadhaarDocUrl' ? selectedAadhaarFileName :
    fieldName === 'identityProofs.panDocUrl' ? selectedPanFileName :
      fieldName.startsWith('educationalCertificates.') ? certificateFileNames[fieldName] || '' :
        fieldName.startsWith('previousEmployerDocuments.') ? previousEmployerFileNames[fieldName] || '' :
          '';

  // Get the actual file name for display
  const getDisplayFileName = () => {
    if (file instanceof File) {
      return file.name;
    } else if (typeof file === 'string' && file) {
      // Extract filename from URL path
      return file.split('/').pop() || file;
    } else if (fileName) {
      return fileName;
    }
    return '';
  };

  const displayFileName = getDisplayFileName();
  const hasError = fileErrors[fieldName] && touchedFiles[fieldName];
  const isUploading = uploadingFiles[fieldName];

  const handleClick = () => {
    const fileInput = document.getElementById(`${fieldName}-upload`) as HTMLInputElement;
    if (fileInput) {
      fileInput.click();
    }
  };

  // In edit mode, show upload area only if no file exists (either new or existing)
  const shouldShowUploadArea = !file || (isEdit && !file);

  return (
    <FormItem>
      <FormLabel>
        {label} {isRequired && <span className="text-red-500">*</span>}
      </FormLabel>
      <FormControl>
        <div className="space-y-2">
          {shouldShowUploadArea ? (
            <div
              className={`border-2 border-dashed rounded-lg p-6 text-center transition-colors ${hasError ? 'border-red-300 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                }`}
              onDragOver={(e) => handleDragOver(e, fieldName)}
              onDragLeave={(e) => handleDragLeave(e, fieldName)}
              onDrop={(e) => handleDrop(e, fieldName)}
              onClick={handleClick}
              style={{ cursor: 'pointer' }}
            >
              <Upload className="mx-auto h-12 w-12 text-gray-400" />
              <div className="mt-4">
                <div className="cursor-pointer">
                  <span className="text-sm font-medium text-blue-600 hover:text-blue-500">
                    Click to upload
                  </span>
                  <span className="text-sm text-gray-500"> or drag and drop</span>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  PDF, JPG, JPEG, PNG up to 10MB
                </p>
                {isEdit && typeof file !== 'object' && (
                  <p className="text-xs text-blue-600 mt-1">
                    Upload a new file to replace the existing document
                  </p>
                )}
              </div>
              <input
                id={`${fieldName}-upload`}
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => handleFileChange(e, fieldName)}
                className="hidden"
                style={{ display: 'none' }}
                key={`${fieldName}-upload-${isEdit ? 'edit' : 'add'}`}
              />
            </div>
          ) : (
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg border">
              <div className="flex items-center space-x-3">
                <Upload className="h-5 w-5 text-gray-500" />
                <div>
                  <p className="text-sm font-medium text-gray-900">{displayFileName}</p>
                  <p className="text-xs text-gray-500">
                    {file instanceof File ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Existing file'}
                  </p>
                  {isEdit && !(file instanceof File) && (
                    <p className="text-xs text-blue-600 mt-1">
                      Upload a new file to replace the existing document
                    </p>
                  )}
                </div>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => removeFile(fieldName)}
                className="text-red-500 hover:text-red-700 hover:bg-red-50 p-1 h-8 w-8 rounded-full"
                disabled={isUploading}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          )}
          {hasError && (
            <p className="text-sm text-red-500">{fileErrors[fieldName]}</p>
          )}
        </div>
      </FormControl>
      <FormMessage />
    </FormItem>
  );
};

// OnboardingForm component
function OnboardingForm({
  initialData,
  onSubmit,
  onCancel,
  isEdit = false,
  candidates: _candidates = [],
  fileUrls,
  setFileUrls,
  formInitialized,
  setFormInitialized,
  onboardingId
}: OnboardingFormProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [uploadingFiles, setUploadingFiles] = useState<{ [key: string]: boolean }>({});
  const [selectedAadhaarFileName, setSelectedAadhaarFileName] = useState('');
  const [selectedPanFileName, setSelectedPanFileName] = useState('');
  const [certificateFileNames, setCertificateFileNames] = useState<{ [key: string]: string }>({});
  const [previousEmployerFileNames, setPreviousEmployerFileNames] = useState<{ [key: string]: string }>({});
  const [candidates, setCandidates] = useState<{ _id: string; name: string; email?: string; phone?: string }[]>([]);
  const [hiredStatusId, setHiredStatusId] = useState<string>('');
  const [fileErrors, setFileErrors] = useState<{ [key: string]: string }>({});
  const [touchedFiles, setTouchedFiles] = useState<{ [key: string]: boolean }>({});
  const [showRemoveConfirmation, setShowRemoveConfirmation] = useState(false);
  const [fileToRemove, setFileToRemove] = useState<string>('');
  const [fileNameToRemove, setFileNameToRemove] = useState<string>('');

  // Initialize file names from initial data if editing
  useEffect(() => {
    if (initialData?.identityProofs?.aadhaarDocUrl) {
      setSelectedAadhaarFileName(initialData.identityProofs.aadhaarDocUrl);
    } else {
      setSelectedAadhaarFileName('');
    }
    if (initialData?.identityProofs?.panDocUrl) {
      setSelectedPanFileName(initialData.identityProofs.panDocUrl);
    } else {
      setSelectedPanFileName('');
    }
    // Initialize educational certificate file names
    if (initialData?.educationalCertificates) {
      const certificateNames: { [key: string]: string } = {};
      initialData.educationalCertificates.forEach((cert, index) => {
        if (cert.docUrl) {
          certificateNames[`educationalCertificates.${index}.docUrl`] = cert.docUrl;
        } else {
          certificateNames[`educationalCertificates.${index}.docUrl`] = '';
        }
      });
      setCertificateFileNames(certificateNames);
    } else {
      setCertificateFileNames({});
    }

    // Initialize previous employer document file names
    if (initialData?.previousEmployerDocuments) {
      const previousEmployerNames: { [key: string]: string } = {};
      if (initialData.previousEmployerDocuments.expLetterUrl) {
        previousEmployerNames['previousEmployerDocuments.expLetterUrl'] = initialData.previousEmployerDocuments.expLetterUrl;
      }
      if (initialData.previousEmployerDocuments.relievingLetterUrl) {
        previousEmployerNames['previousEmployerDocuments.relievingLetterUrl'] = initialData.previousEmployerDocuments.relievingLetterUrl;
      }
      if (initialData.previousEmployerDocuments.resignationAcceptanceUrl) {
        previousEmployerNames['previousEmployerDocuments.resignationAcceptanceUrl'] = initialData.previousEmployerDocuments.resignationAcceptanceUrl;
      }
      if (initialData.previousEmployerDocuments.firstMonthSalarySlipUrl) {
        previousEmployerNames['previousEmployerDocuments.firstMonthSalarySlipUrl'] = initialData.previousEmployerDocuments.firstMonthSalarySlipUrl;
      }
      if (initialData.previousEmployerDocuments.secondMonthSalarySlipUrl) {
        previousEmployerNames['previousEmployerDocuments.secondMonthSalarySlipUrl'] = initialData.previousEmployerDocuments.secondMonthSalarySlipUrl;
      }
      if (initialData.previousEmployerDocuments.thirdMonthSalarySlipUrl) {
        previousEmployerNames['previousEmployerDocuments.thirdMonthSalarySlipUrl'] = initialData.previousEmployerDocuments.thirdMonthSalarySlipUrl;
      }
      setPreviousEmployerFileNames(previousEmployerNames);
    } else {
      setPreviousEmployerFileNames({});
    }
  }, [initialData]);

  // Fetch candidate statuses to get the dynamic "hired" status
  useEffect(() => {
    const fetchCandidateStatuses = async () => {
      try {
        const statuses = await getStatusesByCategory('candidate');

        // Find the "hired" status dynamically
        const hiredStatus = statuses.find(status =>
          status.name.toLowerCase() === 'hired' ||
          status.id.toLowerCase() === 'hired'
        );

        if (hiredStatus) {
          setHiredStatusId(hiredStatus.id);
        } else {
          // Fallback to hardcoded "hired" if not found
          setHiredStatusId('hired');
        }
      } catch (error) {
        // Fallback to hardcoded "hired" if API fails
        setHiredStatusId('hired');
      }
    };

    fetchCandidateStatuses();
  }, []);

  // Fetch candidates with hired status
  useEffect(() => {
    const fetchCandidates = async () => {
      try {
        const response = await candidateService.getCandidates();

        // Use dynamic hired status ID for filtering
        const availableCandidates = (response.data || []).filter((c: any) => {
          if (!hiredStatusId) return false; // Wait for status to be loaded

          // Check if candidate has a status object with _id
          if (c.status && typeof c.status === 'object' && c.status._id) {
            const candidateStatusId = c.status._id;
            return candidateStatusId === hiredStatusId;
          }

          // Fallback: check if status is a string
          if (typeof c.status === 'string') {
            return c.status.toLowerCase() === hiredStatusId.toLowerCase() || c.status.toLowerCase() === 'hired';
          }

          return false;
        });

        // Get existing onboarding records to filter out candidates who already have onboarding
        try {
          const onboardingResponse = await import('@/services/onboarding.service').then(module => module.getAllOnboardings());

          // Ensure we have an array of onboardings
          let existingOnboardings = [];
          if (onboardingResponse && onboardingResponse.data) {
            // Handle different possible response structures
            if (Array.isArray(onboardingResponse.data)) {
              existingOnboardings = onboardingResponse.data;
            } else if (onboardingResponse.data.onboardings && Array.isArray(onboardingResponse.data.onboardings)) {
              existingOnboardings = onboardingResponse.data.onboardings;
            } else if (onboardingResponse.data.data && Array.isArray(onboardingResponse.data.data)) {
              existingOnboardings = onboardingResponse.data.data;
            } else {
              existingOnboardings = [];
            }
          }

          const onboardedCandidateIds = new Set(
            existingOnboardings.map((o: any) => o.candidate?._id || o.candidateId || o.candidate).filter(Boolean)
          );

          // Filter out candidates who already have onboarding
          const availableForOnboarding = availableCandidates.filter((c: any) => !onboardedCandidateIds.has(c._id));

          const mappedCandidates = availableForOnboarding.map((c: any) => ({
            _id: c._id,
            name: c.name || ((c as any).firstName && (c as any).lastName ? `${(c as any).firstName} ${(c as any).lastName}` : (c as any).firstName || (c as any).lastName || 'Unknown Candidate'),
            email: c.email,
            phone: c.phone
          }));
          setCandidates(mappedCandidates);

          // If we're in edit mode and have initial data, add the current candidate to the list
          if (isEdit && initialData?.candidate?._id) {
            const currentCandidate = mappedCandidates.find(c => c._id === initialData.candidate!._id);
            if (!currentCandidate) {
              // Add the current candidate to the list if not already present
              const candidateToAdd = {
                _id: initialData.candidate!._id,
                name: initialData.candidate!.name ||
                  ((initialData as any).candidate?.firstName && (initialData as any).candidate?.lastName
                    ? `${(initialData as any).candidate.firstName} ${(initialData as any).candidate.lastName}`
                    : 'Unknown Candidate'),
                email: '',
                phone: ''
              };
              setCandidates(prev => [candidateToAdd, ...prev]);
            }
          }

          if (mappedCandidates.length === 0 && !isEdit) {

          }
        } catch (onboardingError) {
          // If we can't fetch existing onboardings, show all hired candidates
          const mappedCandidates = availableCandidates.map((c: any) => ({
            _id: c._id,
            name: c.name || ((c as any).firstName && (c as any).lastName ? `${(c as any).firstName} ${(c as any).lastName}` : (c as any).firstName || (c as any).lastName || 'Unknown Candidate'),
            email: c.email,
            phone: c.phone
          }));
          setCandidates(mappedCandidates);
        }
      } catch (error) {
        toast({ title: 'Error', description: 'Failed to fetch candidates', variant: 'destructive' });
      }
    };

    // Only fetch candidates when we have the hired status ID
    if (hiredStatusId) {
      fetchCandidates();
    }
  }, [hiredStatusId]);

  const form = useForm<OnboardingFormValues>({
    resolver: zodResolver(onboardingSchema),
    mode: isEdit ? 'onBlur' : 'onChange',
    defaultValues: {
      candidate: { _id: '', name: '' },
      companyEmail: '',
      previousEmployerDocuments: {
        expLetterUrl: '',
        expLetterExpanded: false,
        relievingLetterUrl: '',
        relievingLetterExpanded: false,
        resignationAcceptanceUrl: '',
        resignationAcceptanceExpanded: false,
        firstMonthSalarySlipUrl: '',
        firstMonthSalarySlipExpanded: false,
        secondMonthSalarySlipUrl: '',
        secondMonthSalarySlipExpanded: false,
        thirdMonthSalarySlipUrl: '',
        thirdMonthSalarySlipExpanded: false,
      },
      identityProofs: {
        aadhaarNumber: '',
        aadhaarDocUrl: '',
        panNumber: '',
        panDocUrl: '',
      },
      educationalCertificates: initialData?.educationalCertificates || [],
      tasks: initialData?.tasks || [],
      notes: initialData?.notes || '',
    },
  });

  const { watch, setValue, getValues } = form;

  // Reset form when initialData changes (for edit mode) - only run once when dialog opens
  useEffect(() => {
    if (initialData && isEdit && !formInitialized) {
      const normalizedData = normalizeOnboardingData(initialData);

      // Only reset if the form is empty or if we're initializing for the first time
      const currentValues = form.getValues();
      const isFormEmpty = !currentValues.candidate._id && !currentValues.companyEmail;

      if (isFormEmpty) {
        // Check if there are existing previous employer documents and expand individual sections
        if (normalizedData.previousEmployerDocuments) {
          const existingDocs = normalizedData.previousEmployerDocuments;
          normalizedData.previousEmployerDocuments = {
            ...existingDocs,
            // Auto-expand individual sections that have documents
            expLetterExpanded: Boolean(existingDocs.expLetterUrl),
            relievingLetterExpanded: Boolean(existingDocs.relievingLetterUrl),
            resignationAcceptanceExpanded: Boolean(existingDocs.resignationAcceptanceUrl),
            firstMonthSalarySlipExpanded: Boolean(existingDocs.firstMonthSalarySlipUrl),
            secondMonthSalarySlipExpanded: Boolean(existingDocs.secondMonthSalarySlipUrl),
            thirdMonthSalarySlipExpanded: Boolean(existingDocs.thirdMonthSalarySlipUrl),
          };
        }

        // Ensure identity proofs are properly initialized
        if (normalizedData.identityProofs) {
          normalizedData.identityProofs = {
            aadhaarNumber: normalizedData.identityProofs.aadhaarNumber || '',
            aadhaarDocUrl: normalizedData.identityProofs.aadhaarDocUrl || '',
            panNumber: normalizedData.identityProofs.panNumber || '',
            panDocUrl: normalizedData.identityProofs.panDocUrl || '',
          };
        }

        // Ensure candidate data is properly set
        if (normalizedData.candidate) {
          normalizedData.candidate = {
            _id: normalizedData.candidate._id || '',
            name: normalizedData.candidate.name || '',
          };
        }

        form.reset(normalizedData);

        // Set file URLs for educational certificates
        if (normalizedData.educationalCertificates) {
          const certificateFileUrls: { [key: string]: string } = {};
          normalizedData.educationalCertificates.forEach((cert, index) => {
            if (cert.docUrl) {
              certificateFileUrls[`educationalCertificates.${index}.docUrl`] = cert.docUrl;
            }
          });
          setFileUrls(prev => ({ ...prev, ...certificateFileUrls }));
        }

        // Also set the file URLs for identity proofs
        const identityFileUrls: { [key: string]: string } = {};
        if (normalizedData.identityProofs?.aadhaarDocUrl) {
          identityFileUrls['identityProofs.aadhaarDocUrl'] = normalizedData.identityProofs.aadhaarDocUrl;
        }
        if (normalizedData.identityProofs?.panDocUrl) {
          identityFileUrls['identityProofs.panDocUrl'] = normalizedData.identityProofs.panDocUrl;
        }
        setFileUrls(prev => ({ ...prev, ...identityFileUrls }));

        // Set file URLs for previous employer documents
        const previousEmployerFileUrls: { [key: string]: string } = {};
        if (normalizedData.previousEmployerDocuments?.expLetterUrl) {
          previousEmployerFileUrls['previousEmployerDocuments.expLetterUrl'] = normalizedData.previousEmployerDocuments.expLetterUrl;
        }
        if (normalizedData.previousEmployerDocuments?.relievingLetterUrl) {
          previousEmployerFileUrls['previousEmployerDocuments.relievingLetterUrl'] = normalizedData.previousEmployerDocuments.relievingLetterUrl;
        }
        if (normalizedData.previousEmployerDocuments?.resignationAcceptanceUrl) {
          previousEmployerFileUrls['previousEmployerDocuments.resignationAcceptanceUrl'] = normalizedData.previousEmployerDocuments.resignationAcceptanceUrl;
        }
        if (normalizedData.previousEmployerDocuments?.firstMonthSalarySlipUrl) {
          previousEmployerFileUrls['previousEmployerDocuments.firstMonthSalarySlipUrl'] = normalizedData.previousEmployerDocuments.firstMonthSalarySlipUrl;
        }
        if (normalizedData.previousEmployerDocuments?.secondMonthSalarySlipUrl) {
          previousEmployerFileUrls['previousEmployerDocuments.secondMonthSalarySlipUrl'] = normalizedData.previousEmployerDocuments.secondMonthSalarySlipUrl;
        }
        if (normalizedData.previousEmployerDocuments?.thirdMonthSalarySlipUrl) {
          previousEmployerFileUrls['previousEmployerDocuments.thirdMonthSalarySlipUrl'] = normalizedData.previousEmployerDocuments.thirdMonthSalarySlipUrl;
        }
        setFileUrls(prev => ({ ...prev, ...previousEmployerFileUrls }));

        setFormInitialized(true);

        // Trigger validation after a short delay to ensure form is properly initialized
        setTimeout(() => {
          if (normalizedData.candidate?._id) {
            form.trigger('candidate._id');
          }
        }, 200);
      }
    }
  }, [initialData, isEdit, formInitialized, form, setFileUrls, setFormInitialized]);

  // Reset form when dialog opens in edit mode and ensure candidate is set after candidates are loaded
  useEffect(() => {
    if (isEdit && initialData && candidates.length > 0 && !formInitialized) {
      const normalizedData = normalizeOnboardingData(initialData);

      // Only reset if the form is empty or if we're initializing for the first time
      const currentValues = form.getValues();
      const isFormEmpty = !currentValues.candidate._id && !currentValues.companyEmail;

      if (isFormEmpty) {
        form.reset(normalizedData);
      }

      // Ensure candidate is properly set after candidates are loaded
      if (normalizedData.candidate?._id) {
        setValue('candidate._id', normalizedData.candidate._id);
        setValue('candidate.name', normalizedData.candidate.name || '');

        // Also ensure the candidate exists in the candidates list
        const candidateExists = candidates.find(c => c._id === normalizedData.candidate?._id);
        if (!candidateExists && normalizedData.candidate?._id) {
          const candidateToAdd = {
            _id: normalizedData.candidate._id,
            name: normalizedData.candidate.name || 'Unknown Candidate',
            email: '',
            phone: ''
          };
          setCandidates(prev => [candidateToAdd, ...prev]);
        }
      }
    }
  }, [isEdit, initialData, form, setValue, candidates, formInitialized]);

  // Handle candidate selection when candidates are loaded in edit mode
  useEffect(() => {
    if (isEdit && initialData && candidates.length > 0) {
      const normalizedData = normalizeOnboardingData(initialData);
      const currentCandidateId = getValues('candidate._id');

      // If the form doesn't have the candidate set yet, set it
      if (!currentCandidateId && normalizedData.candidate?._id) {
        setValue('candidate._id', normalizedData.candidate._id);
        setValue('candidate.name', normalizedData.candidate.name || '');
      }

      // Force form validation after setting candidate
      if (normalizedData.candidate?._id) {
        // Trigger validation for the candidate field
        form.trigger('candidate._id');
      }
    }
  }, [candidates, isEdit, initialData, setValue, getValues, form]);

  // Watch form changes and update validation state
  useEffect(() => {
    const subscription = form.watch((_value) => {
      // Form validation is now handled by React Hook Form with mode: 'onChange'
    });
    return () => subscription.unsubscribe();
  }, [form, fileUrls]);

  // Sync file name displays with fileUrls state
  useEffect(() => {
    // Update Aadhaar file name display
    const aadhaarFile = fileUrls['identityProofs.aadhaarDocUrl'];
    if (aadhaarFile) {
      if (aadhaarFile instanceof File) {
        setSelectedAadhaarFileName(aadhaarFile.name);
      } else if (typeof aadhaarFile === 'string') {
        // Extract filename from URL path
        const filename = aadhaarFile.split('/').pop() || aadhaarFile;
        setSelectedAadhaarFileName(filename);
      }
    } else {
      setSelectedAadhaarFileName('');
    }

    // Update PAN file name display
    const panFile = fileUrls['identityProofs.panDocUrl'];
    if (panFile) {
      if (panFile instanceof File) {
        setSelectedPanFileName(panFile.name);
      } else if (typeof panFile === 'string') {
        // Extract filename from URL path
        const filename = panFile.split('/').pop() || panFile;
        setSelectedPanFileName(filename);
      }
    } else {
      setSelectedPanFileName('');
    }

    // Update educational certificate file names
    const newCertificateFileNames: { [key: string]: string } = {};
    Object.keys(fileUrls).forEach(key => {
      if (key.startsWith('educationalCertificates.')) {
        const file = fileUrls[key];
        if (file) {
          if (file instanceof File) {
            newCertificateFileNames[key] = file.name;
          } else if (typeof file === 'string') {
            // Extract filename from URL path
            const filename = file.split('/').pop() || file;
            newCertificateFileNames[key] = filename;
          }
        }
      }
    });
    setCertificateFileNames(newCertificateFileNames);

    // Update previous employer document file names
    const newPreviousEmployerFileNames: { [key: string]: string } = {};
    Object.keys(fileUrls).forEach(key => {
      if (key.startsWith('previousEmployerDocuments.')) {
        const file = fileUrls[key];
        if (file) {
          if (file instanceof File) {
            newPreviousEmployerFileNames[key] = file.name;
          } else if (typeof file === 'string') {
            // Extract filename from URL path
            const filename = file.split('/').pop() || file;
            newPreviousEmployerFileNames[key] = filename;
          }
        }
      }
    });

    setPreviousEmployerFileNames(newPreviousEmployerFileNames);
  }, [fileUrls]);

  // File validation function
  const validateFile = (file: File, _fieldName: string): string | undefined => {
    // Validate file type
    const allowedTypes = ['application/pdf', 'image/jpeg', 'image/jpg', 'image/png'];
    if (!allowedTypes.includes(file.type)) {
      return 'Please upload a PDF, JPG, JPEG, or PNG file';
    }

    // Validate file size (max 10MB)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      return 'File size must be less than 10MB';
    }

    return undefined;
  };

  // File upload handler with validation
  const handleFileUpload = async (file: File, fieldName: string) => {
    try {
      setUploadingFiles((prev: { [key: string]: boolean }) => ({ ...prev, [fieldName]: true }));

      // Validate file
      const error = validateFile(file, fieldName);
      if (error) {
        throw new Error(error);
      }

      // Store the actual file object for later upload
      setFileUrls((prev: { [key: string]: File | string | null }) => {
        const newFileUrls = { ...prev, [fieldName]: file };
        return newFileUrls;
      });

      // Update selected file names for display
      if (fieldName === 'identityProofs.aadhaarDocUrl') {
        setSelectedAadhaarFileName(file.name);
        setValue('identityProofs.aadhaarDocUrl', file.name); // Store filename in form
      } else if (fieldName === 'identityProofs.panDocUrl') {
        setSelectedPanFileName(file.name);
        setValue('identityProofs.panDocUrl', file.name); // Store filename in form
      } else if (fieldName.startsWith('educationalCertificates.')) {
        setCertificateFileNames((prev: { [key: string]: string }) => ({ ...prev, [fieldName]: file.name }));
        setValue(fieldName as any, file.name); // Store filename in form
      } else if (fieldName.startsWith('previousEmployerDocuments.')) {
        setPreviousEmployerFileNames((prev: { [key: string]: string }) => ({ ...prev, [fieldName]: file.name }));
        setValue(fieldName as any, file.name); // Store filename in form
      }

      // Clear any previous errors for this field
      setFileErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[fieldName];
        return newErrors;
      });

      toast({
        title: "File selected successfully",
        description: `${file.name} has been selected and will be uploaded when you save.`,
      });
    } catch (error: any) {
      toast({
        title: "File selection failed",
        description: error.message || "Failed to select file. Please try again.",
        variant: "destructive",
      });

      // Set error for this field
      setFileErrors(prev => ({ ...prev, [fieldName]: error.message }));
    } finally {
      setUploadingFiles((prev: { [key: string]: boolean }) => ({ ...prev, [fieldName]: false }));
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent, _fieldName: string) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.add('border-blue-500', 'bg-blue-50');
  };

  const handleDragLeave = (e: React.DragEvent, _fieldName: string) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');
  };

  const handleDrop = (e: React.DragEvent, fieldName: string) => {
    e.preventDefault();
    e.stopPropagation();
    e.currentTarget.classList.remove('border-blue-500', 'bg-blue-50');

    const files = e.dataTransfer.files;
    if (files.length > 0) {
      handleFileUpload(files[0], fieldName);
    }
    // Mark file as touched for validation
    setTouchedFiles(prev => ({ ...prev, [fieldName]: true }));
  };

  // File change handler
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>, fieldName: string) => {
    const file = e.target.files?.[0];
    if (file) {
      handleFileUpload(file, fieldName);
    }
    // Mark file as touched for validation
    setTouchedFiles(prev => ({ ...prev, [fieldName]: true }));
  };

  // Remove file handler
  const removeFile = async (fieldName: string) => {
    // Get the current file name for confirmation
    const currentFile = fileUrls[fieldName];
    let fileName = '';

    if (currentFile instanceof File) {
      fileName = currentFile.name;
    } else if (typeof currentFile === 'string' && currentFile) {
      fileName = currentFile.split('/').pop() || currentFile;
    }

    // Set the file to remove and show confirmation
    setFileToRemove(fieldName);
    setFileNameToRemove(fileName);
    setShowRemoveConfirmation(true);
  };

  // Confirm file removal
  const confirmRemoveFile = async () => {
    try {
      // Get the current file URL if it exists (for existing files)
      const currentFileUrl = fileUrls[fileToRemove];

      // If this is an existing file (string URL), delete it from backend
      if (typeof currentFileUrl === 'string' && currentFileUrl && isEdit && onboardingId) {
        // Use the removeFileFromOnboarding function which handles both storage deletion and database update
        try {
          const { removeFileFromOnboarding } = await import('@/services/onboarding.service');
          await removeFileFromOnboarding(onboardingId, fileToRemove);

          toast({
            title: "File removed",
            description: "File has been removed from both frontend and backend.",
          });
        } catch (backendError) {
          toast({
            title: "Warning",
            description: "File removed from form but there was an issue removing it from the server.",
            variant: "destructive",
          });
        }
      } else {
        // For new files or when not in edit mode, just clear the frontend
        toast({
          title: "File removed",
          description: "File has been removed from the form.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Warning",
        description: "File removed from form but there was an issue removing it from the server. Please save to complete the removal.",
        variant: "destructive",
      });
    }

    // Clear the frontend state
    setFileUrls(prev => ({ ...prev, [fileToRemove]: '' }));

    if (fileToRemove === 'identityProofs.aadhaarDocUrl') {
      setSelectedAadhaarFileName('');
      setValue('identityProofs.aadhaarDocUrl', '');
    } else if (fileToRemove === 'identityProofs.panDocUrl') {
      setSelectedPanFileName('');
      setValue('identityProofs.panDocUrl', '');
    } else if (fileToRemove.startsWith('educationalCertificates.')) {
      setCertificateFileNames(prev => ({ ...prev, [fileToRemove]: '' }));
      setValue(fileToRemove as any, '');
    } else if (fileToRemove.startsWith('previousEmployerDocuments.')) {
      setPreviousEmployerFileNames(prev => ({ ...prev, [fileToRemove]: '' }));
      // Extract the field name from the path and set the nested value properly
      const fieldNameOnly = fileToRemove.replace('previousEmployerDocuments.', '');
      setValue(`previousEmployerDocuments.${fieldNameOnly}` as any, '');
    }

    // Clear the file input
    const fileInput = document.getElementById(`${fileToRemove}-upload`) as HTMLInputElement;
    if (fileInput) {
      fileInput.value = '';
    }

    // Mark file as touched for validation
    setTouchedFiles(prev => ({ ...prev, [fileToRemove]: true }));

    // Close confirmation dialog
    setShowRemoveConfirmation(false);
    setFileToRemove('');
    setFileNameToRemove('');
  };

  // Cancel file removal
  const cancelRemoveFile = () => {
    setShowRemoveConfirmation(false);
    setFileToRemove('');
    setFileNameToRemove('');
  };

  // Add educational certificate
  const addEducationalCertificate = () => {
    const currentCertificates = getValues('educationalCertificates') || [];
    setValue('educationalCertificates', [
      ...currentCertificates,
      { degree: '', institution: '', docUrl: '' }
    ]);
  };

  // Remove educational certificate
  const removeEducationalCertificate = async (index: number) => {
    try {
      const currentCertificates = getValues('educationalCertificates') || [];
      const certificateToRemove = currentCertificates[index];

      // If this is an existing certificate with a document URL, delete it from backend
      if (certificateToRemove?.docUrl && isEdit && onboardingId) {
        // Use the removeFileFromOnboarding function which handles both storage deletion and database update
        try {
          const { removeFileFromOnboarding } = await import('@/services/onboarding.service');
          await removeFileFromOnboarding(onboardingId, `educationalCertificates.${index}.docUrl`);

          toast({
            title: "Certificate removed",
            description: "Certificate and associated file have been removed.",
          });
        } catch (backendError) {
          toast({
            title: "Warning",
            description: "Certificate removed from form but there was an issue removing the file from the server.",
            variant: "destructive",
          });
        }
      } else {
        toast({
          title: "Certificate removed",
          description: "Certificate has been removed from the form.",
        });
      }
    } catch (error: any) {
      toast({
        title: "Warning",
        description: "Certificate removed from form but there was an issue removing the file from the server.",
        variant: "destructive",
      });
    }

    // Remove the certificate from the form
    const currentCertificates = getValues('educationalCertificates') || [];
    setValue('educationalCertificates', currentCertificates.filter((_, i) => i !== index));

    // Clear the associated file URL
    const fileKey = `educationalCertificates.${index}.docUrl`;
    setFileUrls(prev => ({ ...prev, [fileKey]: '' }));
    setCertificateFileNames(prev => ({ ...prev, [fileKey]: '' }));
  };

  const handleFormSubmit = async (data: OnboardingFormValues) => {
    try {
      setIsSubmitting(true);
      await onSubmit(data);
      toast({
        title: "Success",
        description: isEdit ? "Onboarding updated successfully!" : "Onboarding created successfully!",
      });
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message || "Something went wrong. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  // Function to check if all required fields are filled

  const handleFormError = () => {
    toast({
      title: "Validation Error",
      description: "Please fill all required fields before submitting.",
      variant: "destructive",
    });
  };

  // Additional effect to ensure candidate is properly set in edit mode
  useEffect(() => {
    if (isEdit && initialData && formInitialized) {
      const normalizedData = normalizeOnboardingData(initialData);
      const currentCandidateId = form.getValues('candidate._id');

      // If we have candidate data but the form doesn't have it set, set it
      if (normalizedData.candidate?._id && !currentCandidateId) {

        form.setValue('candidate._id', normalizedData.candidate._id);
        form.setValue('candidate.name', normalizedData.candidate.name || '');

        // Trigger validation after setting the values
        setTimeout(() => {
          form.trigger('candidate._id');
        }, 100);
      }
    }
  }, [isEdit, initialData, formInitialized, form]);

  // Fallback effect to ensure candidate is set even if other effects fail
  useEffect(() => {
    if (isEdit && initialData && candidates.length > 0) {
      const normalizedData = normalizeOnboardingData(initialData);
      const currentCandidateId = form.getValues('candidate._id');

      // If we have candidate data in initialData but form doesn't have it, force set it
      if (normalizedData.candidate?._id && !currentCandidateId) {

        form.setValue('candidate._id', normalizedData.candidate._id);
        form.setValue('candidate.name', normalizedData.candidate.name || '');

        // Trigger validation after setting the values
        setTimeout(() => {
          form.trigger('candidate._id');
        }, 150);
      }
    }
  }, [isEdit, initialData, candidates, form]);

  // Final validation trigger when form is fully loaded in edit mode
  useEffect(() => {
    if (isEdit && formInitialized && candidates.length > 0) {
      const currentCandidateId = form.getValues('candidate._id');

      if (currentCandidateId) {
        // Trigger validation for all fields after a delay to ensure form is stable
        setTimeout(() => {
          form.trigger();
        }, 300);
      }
    }
  }, [isEdit, formInitialized, candidates, form]);

  // Ensure file inputs are properly initialized in edit mode
  useEffect(() => {
    if (isEdit && formInitialized) {
      // Ensure all file inputs are accessible and functional
      const fileInputs = document.querySelectorAll('input[type="file"]');
      fileInputs.forEach((input) => {
        const htmlInput = input as HTMLInputElement;
        if (htmlInput.value === '') {
          // Reset the input to ensure it's functional
          htmlInput.value = '';
        }
      });
    }
  }, [isEdit, formInitialized]);

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(handleFormSubmit, handleFormError)} className="space-y-6">
        {/* Basic Information */}
        <Card>
          <CardHeader>
            <CardTitle>Basic Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {candidates.length === 0 && (
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-4 mb-4">
                <p className="text-yellow-800 text-sm">
                  No candidates with hired status are available for onboarding. Please hire candidates in the hiring section first.
                </p>
              </div>
            )}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="candidate._id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="flex items-center gap-2">
                      <User className="h-4 w-4" />
                      Candidate <span className="text-red-500">*</span>
                    </FormLabel>
                    <FormControl>
                      <Select
                        onValueChange={(value) => {
                          const candidate = candidates.find(c => c._id === value);
                          field.onChange(value);
                          setValue('candidate.name', candidate?.name || '');
                          // Trigger validation after setting the value
                          form.trigger('candidate._id');
                        }}
                        value={field.value || ''}
                        disabled={(isEdit && candidates.length === 0) || (!!field.value && field.value !== '')}
                      >
                        <SelectTrigger className={`w-full h-12 border-2 transition-colors ${(isEdit && candidates.length === 0) || (!!field.value && field.value !== '')
                            ? 'bg-gray-100 border-gray-300 cursor-not-allowed'
                            : 'bg-white border-gray-200 hover:border-blue-300 focus:border-blue-500'
                          }`}>
                          <SelectValue placeholder={isEdit ? "Loading candidate..." : "Search and select a candidate..."} />
                        </SelectTrigger>
                        <SelectContent className="max-h-60">
                          <div className="p-2">
                            <div className="relative mb-2">
                              <Search className="absolute left-2 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                              <input
                                placeholder="Search candidates..."
                                className="w-full pl-8 pr-4 py-2 text-sm border border-gray-200 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                                onChange={() => {
                                  // Add search functionality here if needed
                                }}
                              />
                            </div>
                          </div>
                          {candidates.length === 0 ? (
                            <div className="px-3 py-2 text-sm text-gray-500">
                              <div className="flex items-center gap-2">
                                <User className="h-4 w-4" />
                                {isEdit ? "Loading candidates..." : "No candidates available for onboarding"}
                              </div>
                            </div>
                          ) : (
                            candidates.map((candidate) => (
                              <SelectItem
                                key={candidate._id}
                                value={candidate._id}
                                className="py-3 cursor-pointer hover:bg-blue-50"
                              >
                                <div className="flex items-center gap-3">
                                  <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                                    <User className="h-4 w-4 text-blue-600" />
                                  </div>
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900 truncate">
                                      {candidate.name}
                                    </div>
                                    {candidate.email && (
                                      <div className="text-sm text-gray-500 truncate">
                                        {candidate.email}
                                      </div>
                                    )}
                                  </div>
                                  {field.value === candidate._id && (
                                    <Check className="h-4 w-4 text-blue-600" />
                                  )}
                                </div>
                              </SelectItem>
                            ))
                          )}
                        </SelectContent>
                      </Select>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="companyEmail"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Company Email <span className="text-red-500">*</span></FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="employee@company.com" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>
          </CardContent>
        </Card>

        {/* Identity Proofs */}
        <Card>
          <CardHeader>
            <CardTitle>Identity Proofs</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="identityProofs.aadhaarNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Aadhaar Number <span className="text-red-500">*</span></FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="1234 5678 9012"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="identityProofs.panNumber"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>PAN Number <span className="text-red-500">*</span></FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="ABCDE1234F"
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FileUploadComponent
                fieldName="identityProofs.aadhaarDocUrl"
                label="Aadhaar Document"
                placeholder="Upload Aadhaar document"
                isRequired={true}
                fileUrls={fileUrls}
                setFileUrls={setFileUrls}
                isEdit={isEdit}
                selectedAadhaarFileName={selectedAadhaarFileName}
                selectedPanFileName={selectedPanFileName}
                certificateFileNames={certificateFileNames}
                previousEmployerFileNames={previousEmployerFileNames}
                fileErrors={fileErrors}
                touchedFiles={touchedFiles}
                uploadingFiles={uploadingFiles}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDrop={handleDrop}
                handleFileChange={handleFileChange}
                removeFile={removeFile}
                setValue={setValue}
              />

              <FileUploadComponent
                fieldName="identityProofs.panDocUrl"
                label="PAN Document"
                placeholder="Upload PAN document"
                isRequired={true}
                fileUrls={fileUrls}
                setFileUrls={setFileUrls}
                isEdit={isEdit}
                selectedAadhaarFileName={selectedAadhaarFileName}
                selectedPanFileName={selectedPanFileName}
                certificateFileNames={certificateFileNames}
                previousEmployerFileNames={previousEmployerFileNames}
                fileErrors={fileErrors}
                touchedFiles={touchedFiles}
                uploadingFiles={uploadingFiles}
                handleDragOver={handleDragOver}
                handleDragLeave={handleDragLeave}
                handleDrop={handleDrop}
                handleFileChange={handleFileChange}
                removeFile={removeFile}
                setValue={setValue}
              />
            </div>
          </CardContent>
        </Card>

        {/* Previous Employer Documents */}
        <Card>
          <CardHeader>
            <CardTitle>Previous Employer Documents</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Experience Letter */}
            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-center">
                <h5 className="font-medium">Experience Letter</h5>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setValue('previousEmployerDocuments.expLetterExpanded', !watch('previousEmployerDocuments.expLetterExpanded'))}
                >
                  {watch('previousEmployerDocuments.expLetterExpanded') ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                </Button>
              </div>
              {watch('previousEmployerDocuments.expLetterExpanded') && (
                <FileUploadComponent
                  fieldName="previousEmployerDocuments.expLetterUrl"
                  label=""
                  placeholder="Upload experience letter"
                  isRequired={false}
                  fileUrls={fileUrls}
                  setFileUrls={setFileUrls}
                  isEdit={isEdit}
                  selectedAadhaarFileName={selectedAadhaarFileName}
                  selectedPanFileName={selectedPanFileName}
                  certificateFileNames={certificateFileNames}
                  previousEmployerFileNames={previousEmployerFileNames}
                  fileErrors={fileErrors}
                  touchedFiles={touchedFiles}
                  uploadingFiles={uploadingFiles}
                  handleDragOver={handleDragOver}
                  handleDragLeave={handleDragLeave}
                  handleDrop={handleDrop}
                  handleFileChange={handleFileChange}
                  removeFile={removeFile}
                  setValue={setValue}
                />
              )}
            </div>

            {/* Relieving Letter */}
            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-center">
                <h5 className="font-medium">Relieving Letter</h5>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setValue('previousEmployerDocuments.relievingLetterExpanded', !watch('previousEmployerDocuments.relievingLetterExpanded'))}
                >
                  {watch('previousEmployerDocuments.relievingLetterExpanded') ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                </Button>
              </div>
              {watch('previousEmployerDocuments.relievingLetterExpanded') && (
                <FileUploadComponent
                  fieldName="previousEmployerDocuments.relievingLetterUrl"
                  label=""
                  placeholder="Upload relieving letter"
                  isRequired={false}
                  fileUrls={fileUrls}
                  setFileUrls={setFileUrls}
                  isEdit={isEdit}
                  selectedAadhaarFileName={selectedAadhaarFileName}
                  selectedPanFileName={selectedPanFileName}
                  certificateFileNames={certificateFileNames}
                  previousEmployerFileNames={previousEmployerFileNames}
                  fileErrors={fileErrors}
                  touchedFiles={touchedFiles}
                  uploadingFiles={uploadingFiles}
                  handleDragOver={handleDragOver}
                  handleDragLeave={handleDragLeave}
                  handleDrop={handleDrop}
                  handleFileChange={handleFileChange}
                  removeFile={removeFile}
                  setValue={setValue}
                />
              )}
            </div>

            {/* Resignation Acceptance */}
            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-center">
                <h5 className="font-medium">Resignation Acceptance</h5>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setValue('previousEmployerDocuments.resignationAcceptanceExpanded', !watch('previousEmployerDocuments.resignationAcceptanceExpanded'))}
                >
                  {watch('previousEmployerDocuments.resignationAcceptanceExpanded') ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                </Button>
              </div>
              {watch('previousEmployerDocuments.resignationAcceptanceExpanded') && (
                <FileUploadComponent
                  fieldName="previousEmployerDocuments.resignationAcceptanceUrl"
                  label=""
                  placeholder="Upload resignation acceptance"
                  isRequired={false}
                  fileUrls={fileUrls}
                  setFileUrls={setFileUrls}
                  isEdit={isEdit}
                  selectedAadhaarFileName={selectedAadhaarFileName}
                  selectedPanFileName={selectedPanFileName}
                  certificateFileNames={certificateFileNames}
                  previousEmployerFileNames={previousEmployerFileNames}
                  fileErrors={fileErrors}
                  touchedFiles={touchedFiles}
                  uploadingFiles={uploadingFiles}
                  handleDragOver={handleDragOver}
                  handleDragLeave={handleDragLeave}
                  handleDrop={handleDrop}
                  handleFileChange={handleFileChange}
                  removeFile={removeFile}
                  setValue={setValue}
                />
              )}
            </div>

            {/* First Month Salary Slip */}
            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-center">
                <h5 className="font-medium">First Month Salary Slip</h5>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setValue('previousEmployerDocuments.firstMonthSalarySlipExpanded', !watch('previousEmployerDocuments.firstMonthSalarySlipExpanded'))}
                >
                  {watch('previousEmployerDocuments.firstMonthSalarySlipExpanded') ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                </Button>
              </div>
              {watch('previousEmployerDocuments.firstMonthSalarySlipExpanded') && (
                <FileUploadComponent
                  fieldName="previousEmployerDocuments.firstMonthSalarySlipUrl"
                  label=""
                  placeholder="Upload first month salary slip"
                  isRequired={false}
                  fileUrls={fileUrls}
                  setFileUrls={setFileUrls}
                  isEdit={isEdit}
                  selectedAadhaarFileName={selectedAadhaarFileName}
                  selectedPanFileName={selectedPanFileName}
                  certificateFileNames={certificateFileNames}
                  previousEmployerFileNames={previousEmployerFileNames}
                  fileErrors={fileErrors}
                  touchedFiles={touchedFiles}
                  uploadingFiles={uploadingFiles}
                  handleDragOver={handleDragOver}
                  handleDragLeave={handleDragLeave}
                  handleDrop={handleDrop}
                  handleFileChange={handleFileChange}
                  removeFile={removeFile}
                  setValue={setValue}
                />
              )}
            </div>

            {/* Second Month Salary Slip */}
            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-center">
                <h5 className="font-medium">Second Month Salary Slip</h5>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setValue('previousEmployerDocuments.secondMonthSalarySlipExpanded', !watch('previousEmployerDocuments.secondMonthSalarySlipExpanded'))}
                >
                  {watch('previousEmployerDocuments.secondMonthSalarySlipExpanded') ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                </Button>
              </div>
              {watch('previousEmployerDocuments.secondMonthSalarySlipExpanded') && (
                <FileUploadComponent
                  fieldName="previousEmployerDocuments.secondMonthSalarySlipUrl"
                  label=""
                  placeholder="Upload second month salary slip"
                  isRequired={false}
                  fileUrls={fileUrls}
                  setFileUrls={setFileUrls}
                  isEdit={isEdit}
                  selectedAadhaarFileName={selectedAadhaarFileName}
                  selectedPanFileName={selectedPanFileName}
                  certificateFileNames={certificateFileNames}
                  previousEmployerFileNames={previousEmployerFileNames}
                  fileErrors={fileErrors}
                  touchedFiles={touchedFiles}
                  uploadingFiles={uploadingFiles}
                  handleDragOver={handleDragOver}
                  handleDragLeave={handleDragLeave}
                  handleDrop={handleDrop}
                  handleFileChange={handleFileChange}
                  removeFile={removeFile}
                  setValue={setValue}
                />
              )}
            </div>

            {/* Third Month Salary Slip */}
            <div className="border rounded-lg p-4 space-y-4">
              <div className="flex justify-between items-center">
                <h5 className="font-medium">Third Month Salary Slip</h5>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => setValue('previousEmployerDocuments.thirdMonthSalarySlipExpanded', !watch('previousEmployerDocuments.thirdMonthSalarySlipExpanded'))}
                >
                  {watch('previousEmployerDocuments.thirdMonthSalarySlipExpanded') ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                </Button>
              </div>
              {watch('previousEmployerDocuments.thirdMonthSalarySlipExpanded') && (
                <FileUploadComponent
                  fieldName="previousEmployerDocuments.thirdMonthSalarySlipUrl"
                  label=""
                  placeholder="Upload third month salary slip"
                  isRequired={false}
                  fileUrls={fileUrls}
                  setFileUrls={setFileUrls}
                  isEdit={isEdit}
                  selectedAadhaarFileName={selectedAadhaarFileName}
                  selectedPanFileName={selectedPanFileName}
                  certificateFileNames={certificateFileNames}
                  previousEmployerFileNames={previousEmployerFileNames}
                  fileErrors={fileErrors}
                  touchedFiles={touchedFiles}
                  uploadingFiles={uploadingFiles}
                  handleDragOver={handleDragOver}
                  handleDragLeave={handleDragLeave}
                  handleDrop={handleDrop}
                  handleFileChange={handleFileChange}
                  removeFile={removeFile}
                  setValue={setValue}
                />
              )}
            </div>
          </CardContent>
        </Card>

        {/* Educational Certificates */}
        <Card>
          <CardHeader>
            <CardTitle>Educational Certificates</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {watch('educationalCertificates')?.map((_cert, index) => (
              <div key={`cert-${index}`} className="border rounded-lg p-4 space-y-4">
                <div className="flex justify-between items-center">
                  <h4 className="font-medium">Certificate {index + 1}</h4>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removeEducationalCertificate(index)}
                  >
                    <X className="h-4 w-4" />
                  </Button>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <FormField
                    control={form.control}
                    name={`educationalCertificates.${index}.degree`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Degree <span className="text-red-500">*</span></FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="Bachelor of Technology" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name={`educationalCertificates.${index}.institution`}
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Institution <span className="text-red-500">*</span></FormLabel>
                        <FormControl>
                          <Input {...field} placeholder="University Name" />
                        </FormControl>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                <FileUploadComponent
                  fieldName={`educationalCertificates.${index}.docUrl`}
                  label="Certificate Document"
                  placeholder="Upload certificate document"
                  isRequired={true}
                  fileUrls={fileUrls}
                  setFileUrls={setFileUrls}
                  isEdit={isEdit}
                  selectedAadhaarFileName={selectedAadhaarFileName}
                  selectedPanFileName={selectedPanFileName}
                  certificateFileNames={certificateFileNames}
                  previousEmployerFileNames={previousEmployerFileNames}
                  fileErrors={fileErrors}
                  touchedFiles={touchedFiles}
                  uploadingFiles={uploadingFiles}
                  handleDragOver={handleDragOver}
                  handleDragLeave={handleDragLeave}
                  handleDrop={handleDrop}
                  handleFileChange={handleFileChange}
                  removeFile={removeFile}
                  setValue={setValue}
                />
              </div>
            ))}

            <Button
              type="button"
              variant="outline"
              onClick={addEducationalCertificate}
              className="w-full"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add Educational Certificate
            </Button>
          </CardContent>
        </Card>

        {/* Additional Information */}
        <Card>
          <CardHeader>
            <CardTitle>Additional Information</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <FormField
              control={form.control}
              name="notes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Notes</FormLabel>
                  <FormControl>
                    <Textarea {...field} placeholder="Additional notes..." />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </CardContent>
        </Card>

        {/* Form Actions */}
        <div className="flex justify-end space-x-2">
          <Button type="button" variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            type="submit"
            disabled={isSubmitting || candidates.length === 0 || !form.formState.isValid}
            className={!form.formState.isValid ? 'opacity-50 cursor-not-allowed' : ''}
          >
            {isSubmitting ? 'Saving...' : (isEdit ? 'Update Onboarding' : 'Create Onboarding')}
          </Button>
        </div>

        {/* File Removal Confirmation Dialog */}
        <Dialog open={showRemoveConfirmation} onOpenChange={setShowRemoveConfirmation}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trash2 className="h-5 w-5 text-red-500" />
                Remove File
              </DialogTitle>
              <DialogDescription className="text-gray-600">
                Are you sure you want to remove <span className="font-medium text-gray-900">"{fileNameToRemove}"</span>?
                <br />
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="gap-2">
              <Button type="button" variant="outline" onClick={cancelRemoveFile} className="flex-1">
                Cancel
              </Button>
              <Button type="button" variant="destructive" onClick={confirmRemoveFile} className="flex-1">
                Remove File
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

      </form>
    </Form>
  );
}

// Define props for the AddOnboardingDialog component
interface AddOnboardingDialogProps {
  mode: 'add' | 'edit';
  onboarding?: OnboardingFormValues;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onOnboardingEdit?: (data: OnboardingFormValues & { fileUrls?: { [key: string]: File | string | null } }) => Promise<void>;
  onRefresh?: () => Promise<void>;
  candidates?: Array<{ _id: string; name: string }>;
}

function AddOnboardingDialog({
  mode = 'add',
  onboarding,
  open,
  onOpenChange,
  onOnboardingEdit,
  onRefresh,
  candidates = []
}: AddOnboardingDialogProps) {
  const [isOpen, setIsOpen] = useState(open || false);
  const [fileUrls, setFileUrls] = useState<{ [key: string]: File | string | null }>({});
  const [formInitialized, setFormInitialized] = useState(false);

  // Reset form initialization when onboarding prop changes
  useEffect(() => {
    if (onboarding) {
      // Reset form initialization to ensure fresh data is loaded
      setFormInitialized(false);
      // Don't reset fileUrls here - this causes issues with file uploads
    }
  }, [(onboarding as any)?._id]); // Only reset when the onboarding ID changes

  // Initialize file URLs from onboarding data if editing
  useEffect(() => {
    if (onboarding && Object.keys(fileUrls).length === 0) {
      // Only initialize if fileUrls is empty (first time opening)
      const initialFileUrls: { [key: string]: File | string | null } = {};
      if (onboarding.identityProofs?.aadhaarDocUrl) {
        initialFileUrls['identityProofs.aadhaarDocUrl'] = onboarding.identityProofs.aadhaarDocUrl;
      }
      if (onboarding.identityProofs?.panDocUrl) {
        initialFileUrls['identityProofs.panDocUrl'] = onboarding.identityProofs.panDocUrl;
      }

      // Add previous employer document file URLs
      if (onboarding.previousEmployerDocuments) {
        if (onboarding.previousEmployerDocuments.expLetterUrl) {
          initialFileUrls['previousEmployerDocuments.expLetterUrl'] = onboarding.previousEmployerDocuments.expLetterUrl;
        }
        if (onboarding.previousEmployerDocuments.relievingLetterUrl) {
          initialFileUrls['previousEmployerDocuments.relievingLetterUrl'] = onboarding.previousEmployerDocuments.relievingLetterUrl;
        }
        if (onboarding.previousEmployerDocuments.resignationAcceptanceUrl) {
          initialFileUrls['previousEmployerDocuments.resignationAcceptanceUrl'] = onboarding.previousEmployerDocuments.resignationAcceptanceUrl;
        }
        if (onboarding.previousEmployerDocuments.firstMonthSalarySlipUrl) {
          initialFileUrls['previousEmployerDocuments.firstMonthSalarySlipUrl'] = onboarding.previousEmployerDocuments.firstMonthSalarySlipUrl;
        }
        if (onboarding.previousEmployerDocuments.secondMonthSalarySlipUrl) {
          initialFileUrls['previousEmployerDocuments.secondMonthSalarySlipUrl'] = onboarding.previousEmployerDocuments.secondMonthSalarySlipUrl;
        }
        if (onboarding.previousEmployerDocuments.thirdMonthSalarySlipUrl) {
          initialFileUrls['previousEmployerDocuments.thirdMonthSalarySlipUrl'] = onboarding.previousEmployerDocuments.thirdMonthSalarySlipUrl;
        }
      }

      if (onboarding.educationalCertificates) {
        onboarding.educationalCertificates.forEach((cert, index) => {
          if (cert.docUrl) {
            initialFileUrls[`educationalCertificates.${index}.docUrl`] = cert.docUrl;
          }
        });
      }
      setFileUrls(initialFileUrls);
    }
  }, [onboarding, fileUrls]);

  // Clear file inputs when dialog opens in edit mode
  useEffect(() => {
    if (isOpen && mode === 'edit') {
      // Don't clear file inputs in edit mode - this prevents file selection
      // The file inputs should remain functional for new file uploads
    }

    // Reset form initialization flag when dialog closes
    if (!isOpen) {
      setFormInitialized(false);
      // Don't reset fileUrls when dialog closes - this causes issues with existing files
    }
  }, [isOpen, mode]);

  useEffect(() => {
    if (open !== undefined) {
      setIsOpen(open);
    }
  }, [open]);

  const handleOpenChange = (newOpen: boolean) => {
    setIsOpen(newOpen);
    if (onOpenChange) {
      onOpenChange(newOpen);
    }
  };

  const handleCancel = () => {
    handleOpenChange(false);
  };

  const handleSubmit = async (data: OnboardingFormValues) => {
    try {
      if (mode === 'edit' && onOnboardingEdit) {
        // For edit mode, include the candidate field but mark it as read-only in the backend

        // Merge existing document URLs from fileUrls into the form data
        // BUT only if the field is not intentionally blank (user hasn't cleared it)
        const mergedData = { ...data };

        // Ensure identityProofs is properly initialized
        if (!mergedData.identityProofs) {
          mergedData.identityProofs = {
            aadhaarNumber: '',
            aadhaarDocUrl: '',
            panNumber: '',
            panDocUrl: '',
          };
        }

        // Merge existing identity proof URLs only if not intentionally blank
        if (fileUrls['identityProofs.aadhaarDocUrl'] && typeof fileUrls['identityProofs.aadhaarDocUrl'] === 'string' &&
          fileUrls['identityProofs.aadhaarDocUrl'] !== '' &&
          (!mergedData.identityProofs?.aadhaarDocUrl || mergedData.identityProofs.aadhaarDocUrl === '')) {
          mergedData.identityProofs = {
            ...mergedData.identityProofs,
            aadhaarDocUrl: fileUrls['identityProofs.aadhaarDocUrl'] as string
          };
        }
        if (fileUrls['identityProofs.panDocUrl'] && typeof fileUrls['identityProofs.panDocUrl'] === 'string' &&
          fileUrls['identityProofs.panDocUrl'] !== '' &&
          (!mergedData.identityProofs?.panDocUrl || mergedData.identityProofs.panDocUrl === '')) {
          mergedData.identityProofs = {
            ...mergedData.identityProofs,
            panDocUrl: fileUrls['identityProofs.panDocUrl'] as string
          };
        }

        // Merge existing previous employer document URLs only if not intentionally blank
        if (!mergedData.previousEmployerDocuments) {
          mergedData.previousEmployerDocuments = {};
        }

        // Handle previous employer documents - respect intentional removals
        const previousEmployerFields = [
          'previousEmployerDocuments.expLetterUrl',
          'previousEmployerDocuments.relievingLetterUrl',
          'previousEmployerDocuments.resignationAcceptanceUrl',
          'previousEmployerDocuments.firstMonthSalarySlipUrl',
          'previousEmployerDocuments.secondMonthSalarySlipUrl',
          'previousEmployerDocuments.thirdMonthSalarySlipUrl'
        ];

        previousEmployerFields.forEach(fieldKey => {
          const fieldName = fieldKey.replace('previousEmployerDocuments.', '') as keyof typeof mergedData.previousEmployerDocuments;
          const fileUrl = fileUrls[fieldKey];

          // Ensure previousEmployerDocuments exists
          if (!mergedData.previousEmployerDocuments) {
            mergedData.previousEmployerDocuments = {};
          }

          // If fileUrls has an empty string, it means user intentionally removed the document
          // Don't merge back the existing URL in this case
          if (fileUrl && typeof fileUrl === 'string' && fileUrl !== '') {
            // Only merge if the form data doesn't already have a value or has an empty string
            if (!mergedData.previousEmployerDocuments[fieldName] || mergedData.previousEmployerDocuments[fieldName] === '') {
              // Use type assertion to handle the assignment
              const docs = mergedData.previousEmployerDocuments as Record<string, any>;
              docs[fieldName] = fileUrl;
            }
          }
          // If fileUrl is empty string, keep the form data as is (empty string = intentional removal)
        });

        // Merge existing educational certificate URLs only if not intentionally blank
        if (mergedData.educationalCertificates) {
          mergedData.educationalCertificates = mergedData.educationalCertificates.map((cert, index) => {
            const existingUrl = fileUrls[`educationalCertificates.${index}.docUrl`];
            if (existingUrl && typeof existingUrl === 'string' && existingUrl !== '' && (!cert.docUrl || cert.docUrl === '')) {
              return { ...cert, docUrl: existingUrl };
            }
            return cert;
          });
        }

        // Pass the merged data along with fileUrls for edit mode
        const editData = {
          ...mergedData,
          candidate: mergedData.candidate, // Ensure candidate field is included
          fileUrls: fileUrls
        };

        // Keep empty strings as empty strings (backend expects empty strings, not null)
        // Don't convert empty strings to null - backend expects empty strings for removed files

        await onOnboardingEdit(editData);

      } else {
        // Files are now optional, no validation needed

        // Validate candidate ID
        if (!data.candidate || !data.candidate._id) {
          throw new Error('Please select a candidate');
        }

        // Create new onboarding
        const formData = new FormData();

        // Prepare the data object that will be sent as JSON string
        const onboardingData: any = {
          candidateId: data.candidate._id,
          companyEmail: data.companyEmail
        };

        // Add identity proofs if they have values
        if (data.identityProofs.aadhaarNumber || data.identityProofs.panNumber) {
          onboardingData.identityProofs = {
            aadhaarNumber: data.identityProofs.aadhaarNumber || '',
            panNumber: data.identityProofs.panNumber || ''
          };
        }

        // Add previous employer documents if they have values
        if (data.previousEmployerDocuments) {
          onboardingData.previousEmployerDocuments = {
            expLetterUrl: data.previousEmployerDocuments.expLetterUrl || '',
            relievingLetterUrl: data.previousEmployerDocuments.relievingLetterUrl || '',
            resignationAcceptanceUrl: data.previousEmployerDocuments.resignationAcceptanceUrl || '',
            firstMonthSalarySlipUrl: data.previousEmployerDocuments.firstMonthSalarySlipUrl || '',
            secondMonthSalarySlipUrl: data.previousEmployerDocuments.secondMonthSalarySlipUrl || '',
            thirdMonthSalarySlipUrl: data.previousEmployerDocuments.thirdMonthSalarySlipUrl || ''
          };
        }



        // Add educational certificates if they exist
        if (data.educationalCertificates && data.educationalCertificates.length > 0) {
          onboardingData.educationalCertificates = data.educationalCertificates.map(cert => ({
            degree: cert.degree || '',
            institution: cert.institution || ''
          }));
        }

        // Add tasks if they exist
        if (data.tasks && data.tasks.length > 0) {
          onboardingData.tasks = data.tasks;
        }

        // Add notes if they exist
        if (data.notes) {
          onboardingData.notes = data.notes;
        }

        // Add the data as a JSON string - this is what the backend expects
        formData.append('data', JSON.stringify(onboardingData));

        // Add files with the correct field names expected by the backend
        const aadhaarFile = fileUrls['identityProofs.aadhaarDocUrl'];
        const panFile = fileUrls['identityProofs.panDocUrl'];

        if (aadhaarFile && aadhaarFile instanceof File) {
          formData.append('identityProofs.aadhaarDocUrl', aadhaarFile);
        }

        if (panFile && panFile instanceof File) {
          formData.append('identityProofs.panDocUrl', panFile);
        }

        // Add educational certificate files
        if (data.educationalCertificates && data.educationalCertificates.length > 0) {
          data.educationalCertificates.forEach((_cert, index) => {
            const certFile = fileUrls[`educationalCertificates.${index}.docUrl`];
            if (certFile && certFile instanceof File) {
              formData.append(`educationalCertificates.${index}.docUrl`, certFile);
            }
          });
        }

        // Add previous employer document files
        const expLetterFile = fileUrls['previousEmployerDocuments.expLetterUrl'];
        const relievingLetterFile = fileUrls['previousEmployerDocuments.relievingLetterUrl'];
        const resignationAcceptanceFile = fileUrls['previousEmployerDocuments.resignationAcceptanceUrl'];
        const firstMonthSalarySlipFile = fileUrls['previousEmployerDocuments.firstMonthSalarySlipUrl'];
        const secondMonthSalarySlipFile = fileUrls['previousEmployerDocuments.secondMonthSalarySlipUrl'];
        const thirdMonthSalarySlipFile = fileUrls['previousEmployerDocuments.thirdMonthSalarySlipUrl'];

        if (expLetterFile && expLetterFile instanceof File) {
          formData.append('expLetter', expLetterFile);
        }

        if (relievingLetterFile && relievingLetterFile instanceof File) {
          formData.append('relievingLetter', relievingLetterFile);
        }

        if (resignationAcceptanceFile && resignationAcceptanceFile instanceof File) {
          formData.append('resignationAcceptance', resignationAcceptanceFile);
        }

        if (firstMonthSalarySlipFile && firstMonthSalarySlipFile instanceof File) {
          formData.append('firstMonthSalarySlip', firstMonthSalarySlipFile);
        }

        if (secondMonthSalarySlipFile && secondMonthSalarySlipFile instanceof File) {
          formData.append('secondMonthSalarySlip', secondMonthSalarySlipFile);
        }

        if (thirdMonthSalarySlipFile && thirdMonthSalarySlipFile instanceof File) {
          formData.append('thirdMonthSalarySlip', thirdMonthSalarySlipFile);
        }

        // Show a toast indicating the request is being processed
        toast({
          title: "Submitting...",
          description: "Creating onboarding record, please wait...",
        });

        // Check authentication token
        const token = getSecureItem('accessToken');
        if (!token) {
          throw new Error('Authentication token not found. Please login again.');
        }

        const result = await createOnboarding(formData);

        // Check if the result contains the expected data
        if (!result || (!result.data && !result._id)) {
          throw new Error('Invalid response from server');
        }

        toast({
          title: "Success",
          description: "Onboarding record created successfully!",
        });
      }

      handleOpenChange(false);

      if (onRefresh) {
        await onRefresh();
      }
    } catch (error: any) {
      console.error('Error in handleSubmit:', error);
      toast({
        title: "Error",
        description: error.message || "Failed to save onboarding data",
        variant: "destructive",
      });
    }
  };


  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="max-w-4xl max-h-[95vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === 'add' ? 'Add New Onboarding' : 'Edit Onboarding'}
          </DialogTitle>
          <DialogDescription className="sr-only">
            {mode === 'add'
              ? 'Form to create a new onboarding process for a hired candidate, including identity proofs and documents.'
              : 'Form to update an existing onboarding record.'}
          </DialogDescription>
        </DialogHeader>
        <OnboardingForm
          initialData={onboarding}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isEdit={mode === 'edit'}
          candidates={candidates}
          fileUrls={fileUrls}
          setFileUrls={setFileUrls}
          formInitialized={formInitialized}
          setFormInitialized={setFormInitialized}
          onboardingId={(onboarding as any)?._id}
        />
      </DialogContent>
    </Dialog>
  );
}

export default AddOnboardingDialog;